import type { TranslationKey } from '@/i18n/translations'
import type { FhVitalsEvent } from './fh-vitals-types'

/**
 * Auto-restart triggers (MAX_MEASURE_TIME > 2 min, FACE_LOSS > 5 s, ABNORMAL_RESULT) and the
 * license-socket close (`_connection_close_`, with close codes 4010/4011/4401 —
 * docs/sdk-techniques/faceheart-technique.md §3) all surface through `on_event`. A patient must be
 * told something useful for each rather than left on a spinner (task brief) — this is that mapping,
 * kept pure and separate from the session runner so every trigger has a named test independent of
 * timers or a running SDK.
 *
 * It names TRANSLATION KEYS and not sentences. The scan surface localises from the token
 * (architecture §9), so a patient enrolled with `language: "es"` is one this app has undertaken to
 * speak Spanish to; a literal here is a paragraph of English in the middle of a Spanish page, and
 * nothing goes red for it. Returning keys also settles the default case, which used to render
 * `event.reason ?? event.state` — the vendor's own close code, shown to a patient.
 */
export interface ScanEventDescription {
  /** One short line, which is the whole of what the patient is shown - scanning-screen.tsx. */
  readonly messageKey: TranslationKey
  /** Whether the SDK itself ends the session for this event (faceheart2.html: `_connection_close_`
   * and `_restart_` both set SCAN_DONE). Anything else is advisory only — the scan continues. */
  readonly endsSession: boolean
}

export function describeScanEvent(event: FhVitalsEvent): ScanEventDescription {
  switch (event.state) {
    case '_connection_close_':
      return {
        messageKey: 'scanInterruptedDetail',
        endsSession: true,
      }
    case '_restart_':
      return {
        messageKey: 'scanRestartDetail',
        endsSession: true,
      }
    case 'FaceLoss':
      return {
        messageKey: 'faceLossDetail',
        endsSession: false,
      }
    case 'Motion':
      return {
        messageKey: 'motionDetail',
        endsSession: false,
      }
    default:
      // An SDK state this app has never seen. The patient gets a sentence they can act on; the
      // vendor's own words go to nobody, because `reason` is a close code or an internal state name
      // and reading one on a medical page is worse than reading nothing.
      return {
        messageKey: 'scanUpdateDetail',
        endsSession: false,
      }
  }
}
